import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import * as wjcGridSheet from "wijmo/wijmo.grid.sheet";
import * as wjXlsx from "wijmo/wijmo.grid.xlsx";
import * as wjcInput from "wijmo/wijmo.input";
import * as wjcGrid from "wijmo/wijmo.grid";
import * as wjcGridSheetChart from "@app/core/wijmo-sheet-chart/wijmo.grid.sheet.chart";
import * as wjcCore from "wijmo/wijmo";
import * as wjcChart from "wijmo/wijmo.chart";
import { ConvertIsoDateService } from "@app/modules/shared/services/convert-iso-date.service";
import { ExplorerService } from "@app/modules/shared/services/explorer.service";
import { Subscription } from "rxjs/Subscription";
import { TranslateService } from "@app/modules/shared/services/translate/translate.service";

declare const $: any;

@Component({
  selector: "kl-flex-sheet",
  templateUrl: "./kl-flex-sheet.component.html",
})
export class KlFlexSheetComponent implements OnInit, OnDestroy {
  constructor(
    private dateConvert: ConvertIsoDateService,
    private translate: TranslateService,
    private explorerService: ExplorerService
  ) {
    this.subscription = explorerService.menuCollapseStream$.subscribe(() => {
      if (this.flexSheet) {
        KlFlexSheetComponent.resizeFlexSheet(this.flexSheet);
      }
    });
    this.selectionFormatState = {};
    this.isFrozen = false;
    this.includeFormulaValues = false;
    const self = this;
    document.body.addEventListener("keydown", (e) => {
      if (self.lbChartTypes) {
        if (e.keyCode === 27) {
          self.lbChartTypes.hostElement.style.display = "none";
        }
        if (
          e.keyCode === 13 &&
          self.lbChartTypes.hostElement.style.display !== "none"
        ) {
          self._addChart();
        }
      }
    });
  }
  @Input() columns: any;
  @Input() data: any;
  @Input() gridName: string;
  undoStack: wjcGridSheet.UndoStack;
  fonts = [
    {
      name: "Arial",
      value: "Arial, Helvetica, sans-serif",
    },
    {
      name: "Arial Black",
      value: '"Arial Black", Gadget, sans-serif',
    },
    {
      name: "Comic Sans MS",
      value: '"Comic Sans MS", cursive, sans-serif',
    },
    {
      name: "Courier New",
      value: '"Courier New", Courier, monospace',
    },
    {
      name: "Georgia",
      value: "Georgia, serif",
    },
    {
      name: "Impact",
      value: "Impact, Charcoal, sans-serif",
    },
    {
      name: "Lucida Console",
      value: '"Lucida Console", Monaco, monospace',
    },
    {
      name: "Lucida Sans Unicode",
      value: '"Lucida Sans Unicode", "Lucida Grande", sans-serif',
    },
    {
      name: "Palatino Linotype",
      value: '"Palatino Linotype", "Book Antiqua", Palatino, serif',
    },
    {
      name: "Tahoma",
      value: "Tahoma, Geneva, sans-serif",
    },
    {
      name: "Segoe UI",
      value: '"Segoe UI", "Roboto", sans-serif',
    },
    {
      name: "Times New Roman",
      value: '"Times New Roman", Times, serif',
    },
    {
      name: "Trebuchet MS",
      value: '"Trebuchet MS", Helvetica, sans-serif',
    },
    {
      name: "Verdana",
      value: "Verdana, Geneva, sans-serif",
    },
  ];
  fontSizeList = [
    {
      name: "8",
      value: "8px",
    },
    {
      name: "9",
      value: "9px",
    },
    {
      name: "10",
      value: "10px",
    },
    {
      name: "11",
      value: "11px",
    },
    {
      name: "12",
      value: "12px",
    },
    {
      name: "14",
      value: "14px",
    },
    {
      name: "16",
      value: "16px",
    },
    {
      name: "18",
      value: "18px",
    },
    {
      name: "20",
      value: "20px",
    },
    {
      name: "22",
      value: "22px",
    },
    {
      name: "24",
      value: "24px",
    },
  ];
  selectionFormatState: wjcGridSheet.IFormatState;
  isFrozen: boolean;
  sortManager: wjcGridSheet.SortManager;
  selection: any = {
    content: "",
    position: "",
    fontFamily: "Arial, Helvetica, sans-serif",
    fontSize: "8px",
  };
  chartTypes = [
    "Column",
    "Bar",
    "Scatter",
    "Line",
    "LineSymbols",
    "Area",
    "Spline",
    "SplineSymbols",
    "SplineArea",
  ];
  selectedTable: wjcGridSheet.Table = null;
  tableStyleNames = null;
  showFieldPanel: boolean;
  includeFormulaValues: boolean;

  private _updatingSelection = false;
  private _updateWithImport = false;
  private _applyFillColor = false;
  private _appliedClass = "";
  private _cellStyleApplying = false;
  private _pendingAction: wjcGridSheet._UndoAction;
  private _chartEngine: wjcGridSheetChart.ChartEngine;

  subscription: Subscription;

  @ViewChild("flexSheet", { static: true }) flexSheet: wjcGridSheet.FlexSheet;

  @ViewChild("cboFontName", { static: true }) cboFontName: wjcInput.ComboBox;

  @ViewChild("cboFontSize", { static: true }) cboFontSize: wjcInput.ComboBox;

  @ViewChild("colorPicker", { static: true }) colorPicker: wjcInput.ColorPicker;

  @ViewChild("lbChartTypes", { static: true }) lbChartTypes: wjcInput.ListBox;

  @ViewChild("cboTableStyles", { static: true })
  cboTableStyles: wjcInput.ComboBox;

  private static _cumulativeOffset(element) {
    let top = 0,
      left = 0;

    do {
      top += element.offsetTop || 0;
      left += element.offsetLeft || 0;
      element = element.offsetParent;
    } while (element);

    return {
      top: top,
      left: left,
    };
  }

  private static resizeFlexSheet(flexSheet: wjcGridSheet.FlexSheet) {
    let excelBookHeight = 0,
      ribbonContainerHeight = 0,
      titleHeight = 0,
      topBoxesHeight = 0;
    const excelBook = <HTMLElement>document.querySelector(".excelbook");
    const topBoxes = <HTMLElement>document.querySelector(".top-boxes");
    const title = <HTMLElement>document.querySelector(".title");
    const ribbonContainer = <HTMLElement>(
      document.querySelector(".ribbon-container")
    );
    if (excelBook) {
      excelBookHeight = excelBook.clientHeight;
    }
    if (ribbonContainer) {
      ribbonContainerHeight = ribbonContainer.offsetHeight;
    }
    if (title) {
      titleHeight = title.offsetHeight;
    }
    if (topBoxes) {
      topBoxesHeight = topBoxes.offsetHeight;
    }
    const spareHeight =
      excelBookHeight -
      titleHeight -
      ribbonContainerHeight -
      topBoxesHeight -
      5;
    flexSheet.hostElement.style.height = spareHeight + "px";
    flexSheet.refresh(true);
  }

  private static _adjustSize() {
    let excelBookHeight = 0,
      ribbonContainerHeight = 0,
      titleHeight = 0,
      topBoxesHeight = 0;
    const excelBook = <HTMLElement>document.querySelector(".excelbook");
    const topBoxes = <HTMLElement>document.querySelector(".top-boxes");
    const title = <HTMLElement>document.querySelector(".title");
    const ribbonContainer = <HTMLElement>(
      document.querySelector(".ribbon-container")
    );
    if (excelBook) {
      excelBookHeight = excelBook.clientHeight;
    }
    if (ribbonContainer) {
      ribbonContainerHeight = ribbonContainer.offsetHeight;
    }
    if (title) {
      titleHeight = title.offsetHeight;
    }
    if (topBoxes) {
      topBoxesHeight = topBoxes.offsetHeight;
    }
    const spareHeight =
      excelBookHeight -
      titleHeight -
      ribbonContainerHeight -
      topBoxesHeight -
      5;
    (<HTMLElement>document.querySelector("#flexsheetContainer")).style.height =
      spareHeight + "px";
  }

  @HostListener("window:resize", ["$event"]) onResize() {
    KlFlexSheetComponent.resizeFlexSheet(this.flexSheet);
  }

  ngOnInit() {
    this.flexSheet = new wjcGridSheet.FlexSheet("#flexSheet");
    this._chartEngine = new wjcGridSheetChart.ChartEngine(this.flexSheet);
    this.undoStack = this.flexSheet.undoStack;
    this.tableStyleNames = [];
    for (let i = 1; i <= 21; i++) {
      this.tableStyleNames.push("TableStyleLight" + i);
    }
    for (let i = 1; i <= 28; i++) {
      this.tableStyleNames.push("TableStyleMedium" + i);
    }
    for (let i = 1; i <= 11; i++) {
      this.tableStyleNames.push("TableStyleDark" + i);
    }

    this.flexSheet.autoGenerateColumns = false;
    //this.flexSheet.allowSorting = false;
    this.flexSheet.showFilterIcons = false;
    this.flexSheet.filter.showFilterIcons = false;
    const sheet1 = new wjcGridSheet.Sheet();
    sheet1.name = "sheet1";
    sheet1.columnCount = this.columns.length;
    sheet1.rowCount = this.data.length;
    // sheet1.itemsSource = itemSource;
    for (let i = 0; i < this.columns.length; i++) {
      sheet1.grid.columns[i].header = this.translate.instant(
        this.columns[i].header
      );
      sheet1.grid.columns[i].binding = this.columns[i].binding;
      sheet1.grid.columns[i].align = this.columns[i].align;
      sheet1.grid.columns[i].format = this.columns[i].format;
      sheet1.grid.columns[i].visible = this.columns[i].visible;
    }
    this.flexSheet.sheets.push(sheet1);
    for (let r = 0; r < this.data.length; r++) {
      for (let c = 0; c < this.columns.length; c++) {
        if (this.columns[c].date) {
          this.flexSheet.setCellData(
            r,
            c,
            new Date(this.data[r][this.columns[c].binding])
          );
        } else if (this.columns[c].translate) {
          this.flexSheet.setCellData(
            r,
            c,
            this.translate.instant(this.data[r][this.columns[c].binding])
          );
        } else {
          this.flexSheet.setCellData(
            r,
            c,
            this.data[r][this.columns[c].binding]
          );
        }
      }
    }
    this.flexSheet.selectedSheetIndex = 0;
    this.flexSheet.deferResizing = true;

    this.flexSheet.deferUpdate(() => {
      // for (let i = 0; i < flexSheet.sheets.length; i++) {
      //   flexSheet.sheets.selectedIndex = i;
      // }
      // flexSheet.selectedSheetIndex = 0;
      // this.columns = this._getColumns();
      // flexSheet.selectedSheet.itemsSource = this.data;
      KlFlexSheetComponent.resizeFlexSheet(this.flexSheet);
      this.sortManager = this.flexSheet.sortManager;
      this._updateSelection(this.flexSheet.selection);
    });
    this.flexSheet.selectedSheetChanged.addHandler(() => {
      if (this.flexSheet.selectedSheet.grid["wj_sheetInfo"]) {
        this._updateFonts(
          this.flexSheet.selectedSheet.grid["wj_sheetInfo"].fonts
        );
      }
      // this.columns = this._getColumns();
      if (!this.sortManager) {
        this.sortManager = this.flexSheet.sortManager;
      }
      this.isFrozen =
        this.flexSheet.frozenRows > 0 || this.flexSheet.frozenColumns > 0;
    });

    this.flexSheet.selectionChanged.addHandler(
      (sender: any, args: wjcGrid.CellRangeEventArgs) => {
        this._updateSelection(args.range);
        this.selectionFormatState = this.flexSheet.getSelectionFormatState();
      }
    );

    this.flexSheet.cellEditEnded.addHandler(
      (sender: any, args: wjcGrid.CellRangeEventArgs) => {
        if (
          args.data &&
          (args.data.keyCode === 46 || args.data.keyCode === 8)
        ) {
          return;
        }
        this._updateSelection(args.range);
      }
    );

    this.flexSheet.undoStack.undoStackChanged.addHandler(() => {
      this._updateSelection(this.flexSheet.selection);
    });

    this.flexSheet.columns.collectionChanged.addHandler(() => {
      // this.columns = this._getColumns();
    });

    this.flexSheet.loaded.addHandler(() => {
      // this.columns = this._getColumns();
      if (this.flexSheet.selectedSheet.grid["wj_sheetInfo"]) {
        this._updateFonts(
          this.flexSheet.selectedSheet.grid["wj_sheetInfo"].fonts
        );
      }
      this._updateSelection(this.flexSheet.selection);
      this.isFrozen =
        this.flexSheet.frozenRows > 0 || this.flexSheet.frozenColumns > 0;
    });

    this.flexSheet.updatedLayout.addHandler(() => {
      if (this.flexSheet.selection && this.flexSheet.selection.isValid) {
        this._getSelectedTable(this.flexSheet.selection);
      } else {
        this.selectedTable = null;
      }
    });
    KlFlexSheetComponent._adjustSize();
  }

  cboFontNameInit(cboFontName: wjcInput.ComboBox) {
    if (cboFontName) {
      cboFontName.selectedIndexChanged.addHandler(() => {
        // apply the font family for the selected cells
        if (!this._updateWithImport) {
          if (!this._updatingSelection) {
            this.flexSheet.applyCellsStyle({
              fontFamily: cboFontName.selectedItem.value,
            });
          }
        } else {
          this._updateWithImport = false;
        }
      });
    }
  }

  cboFontSizeInit(cboFontSize: wjcInput.ComboBox) {
    if (cboFontSize) {
      cboFontSize.selectedIndexChanged.addHandler(() => {
        if (!this._updatingSelection) {
          this.flexSheet.applyCellsStyle({
            fontSize: cboFontSize.selectedItem.value,
          });
        }
      });
    }
  }

  colorPickerInit(colorPicker: wjcInput.ColorPicker) {
    const ua = window.navigator.userAgent;
    let blurEvt: string;

    if (colorPicker) {
      blurEvt = /firefox/i.test(ua) ? "blur" : "focusout";
      colorPicker.hostElement.addEventListener(blurEvt, () => {
        setTimeout(() => {
          if (!colorPicker.containsFocus()) {
            this._applyFillColor = false;
            colorPicker.hostElement.style.display = "none";
          }
        }, 0);
      });

      colorPicker.hostElement.addEventListener("keydown", (e) => {
        if (e.keyCode === 27) {
          colorPicker.hostElement.style.display = "none";
        }
      });

      colorPicker.valueChanged.addHandler(() => {
        if (this._applyFillColor) {
          this.flexSheet.applyCellsStyle({
            backgroundColor: colorPicker.value,
          });
        } else {
          this.flexSheet.applyCellsStyle({ color: colorPicker.value });
        }
      });
    }
  }

  lbChartTypesInit(lbChartTypes: wjcInput.ListBox) {
    if (lbChartTypes) {
      lbChartTypes.hostElement.addEventListener("mouseup", () => {
        setTimeout(() => {
          this._addChart();
        }, 10);
      });
      lbChartTypes.lostFocus.addHandler(() => {
        lbChartTypes.hostElement.style.display = "none";
      });
    }
  }

  cboTableStylesInit(cboTableStyles: wjcInput.ComboBox) {
    if (cboTableStyles) {
      cboTableStyles.selectedIndexChanged.addHandler(() => {
        if (!this._updatingSelection && this.selectedTable) {
          this.selectedTable.style = this.flexSheet.getBuiltInTableStyle(
            cboTableStyles.selectedValue
          );
        }
      });
      if (this.selectedTable) {
        const tableStyle = this.flexSheet.getBuiltInTableStyle(
          this.selectedTable.style.name
        );
        if (tableStyle) {
          cboTableStyles.selectedValue = tableStyle.name;
        }
      }
    }
  }

  // export
  exportExcel() {
    if (this.flexSheet) {
      wjXlsx.FlexGridXlsxConverter.save(
        this.flexSheet,
        {
          includeColumnHeaders: false,
          includeCellStyles: false,
        },
        this.dateConvert.convertDate(new Date()) + "-" + this.gridName + ".xlsx"
      );
    }
  }

  importExcel(event) {
    if (this.flexSheet && event.target.files[0]) {
      this.flexSheet.load(event.target.files[0]);
      event.target.value = "";
    }
  }

  newFile() {
    if (this.flexSheet) {
      this.flexSheet.clear();
    }
  }

  undo() {
    if (this.flexSheet) {
      this.flexSheet.undo();
    }
  }

  redo() {
    if (this.flexSheet) {
      this.flexSheet.redo();
    }
  }

  applyCellTextAlign(textAlign) {
    if (this.flexSheet) {
      this.flexSheet.applyCellsStyle({ textAlign: textAlign });
      this.selectionFormatState.textAlign = textAlign;
    }
  }

  applyBoldStyle() {
    if (this.flexSheet) {
      this.flexSheet.applyCellsStyle({
        fontWeight: this.selectionFormatState.isBold ? "none" : "bold",
      });
      this.selectionFormatState.isBold = !this.selectionFormatState.isBold;
    }
  }

  applyUnderlineStyle() {
    if (this.flexSheet) {
      this.flexSheet.applyCellsStyle({
        textDecoration: this.selectionFormatState.isUnderline
          ? "none"
          : "underline",
      });
      this.selectionFormatState.isUnderline =
        !this.selectionFormatState.isUnderline;
    }
  }

  applyItalicStyle() {
    if (this.flexSheet) {
      this.flexSheet.applyCellsStyle({
        fontStyle: this.selectionFormatState.isItalic ? "none" : "italic",
      });
      this.selectionFormatState.isItalic = !this.selectionFormatState.isItalic;
    }
  }

  mergeCells() {
    if (this.flexSheet) {
      this.flexSheet.mergeRange();
      this.selectionFormatState = this.flexSheet.getSelectionFormatState();
    }
  }

  freeze() {
    if (this.flexSheet) {
      this.flexSheet.freezeAtCursor();
      this.isFrozen =
        this.flexSheet.frozenRows > 0 || this.flexSheet.frozenColumns > 0;
    }
  }

  showFilter() {
    if (this.flexSheet) {
      this.flexSheet.showColumnFilter();
    }
  }

  showColorPicker(e, isFillColor) {
    const offset = KlFlexSheetComponent._cumulativeOffset(e.target);

    if (this.colorPicker) {
      this.colorPicker.hostElement.style.display = "inline";
      this.colorPicker.hostElement.style.left = offset.left + "px";
      this.colorPicker.hostElement.style.top =
        offset.top + e.target.clientHeight + 2 + "px";
      this.colorPicker.hostElement.focus();
    }

    this._applyFillColor = isFillColor;
  }

  applyCellStyle(className?, cancelCellStyle?) {
    if (cancelCellStyle) {
      if (this._cellStyleApplying) {
        this._cellStyleApplying = false;
      } else {
        this.flexSheet.applyCellsStyle(null);
      }
    } else {
      if (className) {
        this._appliedClass = className + "-style";
        this.flexSheet.applyCellsStyle(
          { className: this._appliedClass },
          undefined,
          true
        );
      } else if (this._appliedClass) {
        this.flexSheet.applyCellsStyle({ className: this._appliedClass });
        this._appliedClass = "";
        this._cellStyleApplying = true;
      }
    }
  }

  commitSort() {
    this.sortManager.commitSort();
  }

  cancelSort() {
    this.sortManager.cancelSort();
  }

  addSortLevel() {
    this.sortManager.addSortLevel();
  }

  deleteSortLevel() {
    this.sortManager.deleteSortLevel();
  }

  copySortLevel() {
    this.sortManager.copySortLevel();
  }

  moveSortLevel(offset) {
    this.sortManager.moveSortLevel(offset);
  }

  applySortColumnIndex(e, sortItem) {
    sortItem.columnIndex = +e.target.value;
  }

  applySortAscending(e, sortItem) {
    sortItem.ascending = e.target.value === "true";
  }

  updateSelectionContent(e) {
    const flexSheet = this.flexSheet;
    const selection = flexSheet.selection;

    if (e.keyCode) {
      if (flexSheet.isFunctionListOpen) {
        switch (e.keyCode) {
          case 38:
            flexSheet.selectPreviousFunction();
            e.preventDefault();
            return;
          case 40:
            flexSheet.selectNextFunction();
            e.preventDefault();
            return;
          case 9:
          case 13:
            flexSheet.applyFunctionToCell();
            e.preventDefault();
            return;
          case 27:
            flexSheet.hideFunctionList();
            e.preventDefault();
            return;
        }
      }
      if (e.keyCode !== 13) {
        return;
      }
    }

    if (selection && selection.row > -1 && selection.col > -1) {
      if (flexSheet.isFunctionListOpen) {
        setTimeout(() => {
          flexSheet.hideFunctionList();
        }, 200);
      } else {
        flexSheet.setCellData(
          selection.row,
          selection.col,
          e.target.value,
          true
        );
        if (
          this._pendingAction instanceof wjcGridSheet._EditAction &&
          this._pendingAction.saveNewState()
        ) {
          flexSheet.undoStack._addAction(this._pendingAction);
        }
        this._pendingAction = null;
        flexSheet.refresh(false);
      }
    }
  }

  pendingCellEditAction() {
    this._pendingAction = new wjcGridSheet._EditAction(this.flexSheet);
  }

  showFunctionList(e) {
    if (
      (e.keyCode && e.keyCode !== 27 && (e.keyCode > 40 || e.keyCode < 32)) ||
      !e.keyCode
    ) {
      this.flexSheet.showFunctionList(e.target);
    }
  }

  hidePopup(e) {
    const modals = document.querySelectorAll(".modal");

    if (e.keyCode === 27) {
      if (modals && modals.length > 0) {
        for (let i = 0; i < modals.length; i++) {
          $(modals[i])["modal"]("hide");
        }
      }
    }
  }

  showChartTypes(e) {
    const lbChartTypes = this.lbChartTypes;
    let target = e.target;
    let offset;

    if (!(target instanceof HTMLButtonElement)) {
      target = target.parentElement;
    }
    offset = KlFlexSheetComponent._cumulativeOffset(target);

    if (lbChartTypes) {
      lbChartTypes.selectedIndex = -1;
      lbChartTypes.hostElement.style.display = "inline";
      lbChartTypes.hostElement.style.left = offset.left + "px";
      lbChartTypes.hostElement.style.top =
        offset.top + target.clientHeight + 2 + "px";
      lbChartTypes.hostElement.focus();
    }
  }

  private _updateSelection(sel) {
    const flexSheet = this.flexSheet,
      rowCnt = flexSheet.rows.length,
      colCnt = flexSheet.columns.length;
    let r;
    let c;
    let cellStyle;

    this._updatingSelection = true;
    if (
      sel.row > -1 &&
      sel.col > -1 &&
      rowCnt > 0 &&
      colCnt > 0 &&
      sel.col < colCnt &&
      sel.col2 < colCnt &&
      sel.row < rowCnt &&
      sel.row2 < rowCnt
    ) {
      r = sel.row >= rowCnt ? rowCnt - 1 : sel.row;
      c = sel.col >= colCnt ? colCnt - 1 : sel.col;
      this.selection.content = flexSheet.getCellData(r, c, true);
      this.selection.position =
        wjcGridSheet.FlexSheet.convertNumberToAlpha(sel.col) + (sel.row + 1);
      cellStyle = flexSheet.selectedSheet.getCellStyle(sel.row, sel.col);
      if (cellStyle) {
        this.cboFontName.selectedIndex = this._checkFontfamily(
          cellStyle.fontFamily
        );
        this.cboFontSize.selectedIndex = this._checkFontSize(
          cellStyle.fontSize
        );
      } else {
        this.cboFontName.selectedIndex = 0;
        this.cboFontSize.selectedIndex = 5;
      }

      this._getSelectedTable(sel);
    } else {
      this.selection.content = "";
      this.selection.position = "";
    }

    this._updatingSelection = false;
  }

  // Get selected table in FlexSheet.
  private _getSelectedTable(selection: wjcGrid.CellRange) {
    let rowIndex: number,
      colIndx: number,
      selectedTable: wjcGridSheet.Table,
      tableStyle: wjcGridSheet.TableStyle;

    for (
      rowIndex = selection.topRow;
      rowIndex <= selection.bottomRow;
      rowIndex++
    ) {
      for (
        colIndx = selection.leftCol;
        colIndx <= selection.rightCol;
        colIndx++
      ) {
        selectedTable = this.flexSheet.selectedSheet.findTable(
          rowIndex,
          colIndx
        );
        if (selectedTable) {
          this.selectedTable = selectedTable;
          tableStyle = this.flexSheet.getBuiltInTableStyle(
            selectedTable.style.name
          );
          if (tableStyle && this.cboTableStyles) {
            this.cboTableStyles.selectedValue = tableStyle.name;
          }
          return;
        }
      }
    }
    this.selectedTable = null;
  }

  private _checkFontfamily(fontFamily) {
    let font;
    let fontIndex = 0;
    if (!fontFamily) {
      return fontIndex;
    }
    for (fontIndex < this.fonts.length; fontIndex++; ) {
      font = this.fonts[fontIndex];
      if (font.name === fontFamily || font.value === fontFamily) {
        return fontIndex;
      }
    }
    return 10;
  }

  private _checkFontSize(fontSize) {
    let size;

    if (fontSize === undefined) {
      return 5;
    }
    for (let index = 0; index < this.fontSizeList.length; index++) {
      size = this.fontSizeList[index];
      if (size.value === fontSize || size.name === fontSize) {
        return index;
      }
    }
    return 5;
  }

  private _updateFonts(importedFonts) {
    const fonts = this.fonts.slice();
    let fontIndex, importedFontIndex, font, importedFont, importedFontExisted;

    if (!importedFonts) {
      return;
    }

    if (fonts.length > 14) {
      fonts.splice(14, fonts.length - 14);
    }

    for (
      importedFontIndex = 0;
      importedFontIndex < importedFonts.length;
      importedFontIndex++
    ) {
      importedFont = importedFonts[importedFontIndex];
      for (fontIndex = 0; fontIndex < fonts.length; fontIndex++) {
        font = fonts[fontIndex];
        if (font.name === importedFont || font.value === importedFont) {
          importedFontExisted = true;
          break;
        }
      }
      if (!importedFontExisted) {
        fonts.push({
          name: importedFont,
          value: importedFont,
        });
      }
      importedFontExisted = false;
    }

    this._updateWithImport = true;
    this.fonts = fonts;
    this.cboFontName.itemsSource = fonts;
  }

  private _addChart() {
    let chartType = null;
    if (this.lbChartTypes.selectedValue != null) {
      chartType = wjcCore.asEnum(
        this.lbChartTypes.selectedValue,
        wjcChart.ChartType
      );
    }
    this._chartEngine.addChart(chartType);
    this.lbChartTypes.hostElement.style.display = "none";
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
